import React, { useState, useEffect, useRef } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../../../store/slices/authSlice';
import { emitSessionExpired } from '../../../services/sessionEvents.js';
// Removed missing Modal import, using inline styles instead
// user rules don't specify component library. 
// I'll assume standard HTML/CSS for the modal to be safe and avoid dependencies.

const SessionTimeout = () => {
    const dispatch = useDispatch();
    const { user, isAuthenticated } = useSelector((state) => state.auth);
    const [showWarning, setShowWarning] = useState(false);
    const [secondsRemaining, setSecondsRemaining] = useState(0);
    const [isRedirecting, setIsRedirecting] = useState(false);
    const logoutTimerRef = useRef(null);
    const suppressAutoLogoutRef = useRef(false);
    const warningModalRef = useRef(null);

    // Get timeout from user settings or default to 30 minutes.
    // Supports numeric minutes and time strings like "00:30:00".
    // Clamp to avoid browser timer overflow and invalid values from backend/user settings.
    const SESSION_MIN_MINUTES = 1;
    const SESSION_MAX_MINUTES = 1440; // 24 hours
    const normalizeSessionMinutes = (rawValue, fallback = 30) => {
        const clamp = (minutes) => Math.min(Math.max(minutes, SESSION_MIN_MINUTES), SESSION_MAX_MINUTES);

        if (rawValue == null) return clamp(fallback);

        if (typeof rawValue === 'number' && Number.isFinite(rawValue)) {
            return clamp(Math.floor(rawValue));
        }

        const text = String(rawValue).trim();
        if (!text) return clamp(fallback);

        // Handle database TIME-like values: HH:MM or HH:MM:SS
        if (text.includes(':')) {
            const parts = text.split(':').map((part) => Number.parseInt(part, 10));
            if (parts.every((part) => Number.isFinite(part) && part >= 0)) {
                const [hours = 0, minutes = 0, seconds = 0] = parts;
                const totalMinutes = Math.floor((hours * 3600 + minutes * 60 + seconds) / 60);
                return clamp(totalMinutes || fallback);
            }
        }

        const parsed = Number.parseInt(text, 10);
        if (Number.isFinite(parsed)) {
            return clamp(parsed);
        }

        return clamp(fallback);
    };

    const parsedSessionMinutes = normalizeSessionMinutes(user?.session_time, 30);
    const timeoutMinutes = Number.isFinite(parsedSessionMinutes)
        ? Math.min(Math.max(parsedSessionMinutes, SESSION_MIN_MINUTES), SESSION_MAX_MINUTES)
        : 30;
    const timeoutMs = timeoutMinutes * 60 * 1000;

    // Always show warning at half of configured session time.
    const warningMs = Math.floor(timeoutMs / 2);

    const onIdle = () => {
        handleLogout();
    };

    const onActive = () => {
        if (logoutTimerRef.current) {
            clearTimeout(logoutTimerRef.current);
            logoutTimerRef.current = null;
        }
        setShowWarning(false);
    };

    const handleLogout = () => {
        if (suppressAutoLogoutRef.current) return;

        if (logoutTimerRef.current) {
            clearTimeout(logoutTimerRef.current);
            logoutTimerRef.current = null;
        }

        setShowWarning(false);
        setIsRedirecting(true);
        
        // Let the central App handler take over the smooth navigation
        logoutTimerRef.current = setTimeout(() => {
            if (!suppressAutoLogoutRef.current) {
                emitSessionExpired('expired');
            }
            logoutTimerRef.current = null;
        }, 300);
    };

    const handleStayLoggedIn = () => {
        if (logoutTimerRef.current) {
            clearTimeout(logoutTimerRef.current);
            logoutTimerRef.current = null;
        }

        suppressAutoLogoutRef.current = false;
        setShowWarning(false);
        setIsRedirecting(false);
        reset();
    };

    const { getRemainingTime, reset } = useIdleTimer({
        timeout: timeoutMs,
        onIdle,
        onActive,
        debounce: 500,
        crossTab: true,
        syncTimers: 200
    });

    useEffect(() => {
        if (!isAuthenticated) return;

        // Fresh login should always start with a clean idle timer state.
        if (logoutTimerRef.current) {
            clearTimeout(logoutTimerRef.current);
            logoutTimerRef.current = null;
        }
        suppressAutoLogoutRef.current = false;
        setShowWarning(false);
        setIsRedirecting(false);
        reset();
    }, [isAuthenticated, user?.session_time, reset]);

    useEffect(() => {
        const interval = setInterval(() => {
            if (!isAuthenticated) return;

            if (showWarning) {
                setSecondsRemaining((prev) => Math.max(prev - 1, 0));
                return;
            }

            const remaining = getRemainingTime();

            if (remaining <= warningMs && !showWarning) {
                // Start warning timer from exact configured warning window (e.g., 30s for 1 minute session).
                setSecondsRemaining(Math.ceil(warningMs / 1000));
                setShowWarning(true);
                return;
            }

            setSecondsRemaining(Math.ceil(remaining / 1000));
        }, 1000);

        return () => clearInterval(interval);
    }, [isAuthenticated, getRemainingTime, showWarning, warningMs]);

    useEffect(() => {
        if (!showWarning) return;
        if (secondsRemaining > 0) return;
        handleLogout();
    }, [showWarning, secondsRemaining]);

    useEffect(() => {
        return () => {
            if (logoutTimerRef.current) {
                clearTimeout(logoutTimerRef.current);
                logoutTimerRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        if (!showWarning) return undefined;

        // Force-close any active control (including open native selects) behind the modal.
        if (document.activeElement && typeof document.activeElement.blur === 'function') {
            document.activeElement.blur();
        }

        const previousOverflow = document.body.style.overflow;
        const previousPointerEvents = document.body.style.pointerEvents;
        document.body.style.overflow = 'hidden';
        document.body.style.pointerEvents = 'auto';

        // Capture focus in the session modal so background UI can't retain focus.
        const focusTimer = setTimeout(() => {
            warningModalRef.current?.focus();
        }, 0);

        return () => {
            clearTimeout(focusTimer);
            document.body.style.overflow = previousOverflow;
            document.body.style.pointerEvents = previousPointerEvents;
        };
    }, [showWarning]);

    if (!isAuthenticated) return null;

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    return (
        <>
            {/* Visual Countdown Badge - Always visible while logged in */}
            {/* 
            <div style={{
                position: 'fixed',
                bottom: '20px',
                right: '20px',
                backgroundColor: secondsRemaining < 60 ? '#dc3545' : '#198754',
                color: 'white',
                padding: '10px 15px',
                borderRadius: '30px',
                fontSize: '14px',
                fontWeight: 'bold',
                boxShadow: '0 4px 10px rgba(0,0,0,0.2)',
                zIndex: 9998,
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'background-color 0.3s ease'
            }}>
                <i className="fa fa-clock-o"></i>
                <span>Session Expires: {formatTime(secondsRemaining)}</span>
            </div>
            */}

            {/* Warning Modal */}
            {showWarning && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 2147483647,
                    backdropFilter: 'blur(4px)'
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        padding: '2.5rem',
                        borderRadius: '12px',
                        maxWidth: '450px',
                        width: '90%',
                        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
                        textAlign: 'center',
                        animation: 'fadeIn 0.3s ease'
                    }}
                        ref={warningModalRef}
                        tabIndex={-1}
                    >
                        <div style={{
                            width: '60px',
                            height: '60px',
                            backgroundColor: '#fff3cd',
                            color: '#856404',
                            borderRadius: '50%',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            fontSize: '30px',
                            margin: '0 auto 1.5rem auto'
                        }}>
                            <i className="fa fa-exclamation-triangle"></i>
                        </div>
                        <h2 style={{ marginTop: 0, color: '#333', fontSize: '24px' }}>Session Expiring</h2>
                        <p style={{ color: '#666', margin: '1rem 0', fontSize: '16px', lineHeight: '1.5' }}>
                            Your session will expire in <strong style={{ color: '#dc3545', fontSize: '18px' }}>{formatTime(secondsRemaining)}</strong> due to inactivity. 
                            Would you like to stay logged in?
                        </p>
                        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', marginTop: '2rem' }}>
                            <button
                                onClick={handleLogout}
                                style={{
                                    padding: '0.75rem 1.5rem',
                                    border: '1px solid #dee2e6',
                                    backgroundColor: 'white',
                                    color: '#495057',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontWeight: '600',
                                    transition: 'all 0.2s'
                                }}
                            >
                                Logout Now
                            </button>
                            <button
                                onClick={handleStayLoggedIn}
                                style={{
                                    padding: '0.75rem 1.5rem',
                                    border: 'none',
                                    backgroundColor: '#0d6efd',
                                    color: 'white',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontWeight: '600',
                                    boxShadow: '0 4px 6px rgba(13, 110, 253, 0.3)',
                                    transition: 'all 0.2s'
                                }}
                            >
                                Stay Logged In
                            </button>
                        </div>
                        <style>{`
                            @keyframes fadeIn {
                                from { opacity: 0; transform: translateY(-20px); }
                                to { opacity: 1; transform: translateY(0); }
                            }
                        `}</style>
                    </div>
                </div>
            )}
        </>
    );
};

export default SessionTimeout;
